import React, { useEffect, useState } from 'react';
import { getRiskCateById, deleteRiskCate } from '../../api/riskCateApi';
import {
  CATEGORY_TYPE_OPTIONS, parseProcessList,
} from '../../utils/riskUtils';
import {
  Card, CardContent, Typography, Divider, Stack, Box, Button, Avatar, Chip,
} from '@mui/material';
import {
  Category,
  Tag,
  AccountTree,
  Sort,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

function InfoRow({ icon, label, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ width: 130, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#495057' }}>{label}</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
}

function RiskCateView({ id, token, comCode, onEdit, onDeleted }) {
  const [item, setItem] = useState(null);

  useEffect(() => {
    setItem(null);
    getRiskCateById(id, token, comCode)
      .then(data => setItem(data))
      .catch(err => console.error('분류 조회 실패:', err));
  }, [id, token, comCode]);

  if (!item) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
        <Typography variant="body2">불러오는 중...</Typography>
      </Box>
    );
  }

  const handleDelete = async () => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await deleteRiskCate(id, token, comCode);
      alert('삭제되었습니다.');
      if (onDeleted) onDeleted();
    } catch (err) {
      alert('삭제 실패: ' + err.message);
    }
  };

  const categoryTypeLabel = CATEGORY_TYPE_OPTIONS.find(o => o.value === item.category_type)?.label || '-';
  const processLabels = parseProcessList(item.process_list);

  return (
    <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e4e8', bgcolor: '#fff' }}>
      <Box sx={{ height: 6, bgcolor: '#1976d2' }} />
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* 헤더 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}>
              <Category />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                {item.risk_category_num}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                위험성 분류 상세 정보
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* 정보 */}
          <Stack spacing={2.5}>
            <InfoRow icon={<Tag fontSize="small" sx={{ color: '#adb5bd' }} />} label="분류 넘버">
              <Typography variant="body2">{item.risk_category_num || '-'}</Typography>
            </InfoRow>

            <InfoRow icon={<Category fontSize="small" sx={{ color: '#adb5bd' }} />} label="분류 내용">
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {item.risk_category_context || '-'}
              </Typography>
            </InfoRow>

            <InfoRow icon={<FolderIcon fontSize="small" sx={{ color: '#adb5bd' }} />} label="타입">
              {item.category_type ? (
                <Chip
                  label={categoryTypeLabel}
                  size="small"
                  color={item.category_type === 'folder' ? 'warning' : 'info'}
                  variant="outlined"
                />
              ) : (
                <Typography variant="body2">-</Typography>
              )}
            </InfoRow>

            <InfoRow icon={<AssignmentIcon fontSize="small" sx={{ color: '#adb5bd' }} />} label="평가 공정">
              {processLabels.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {processLabels.map((label, idx) => (
                    <Chip key={idx} label={label} size="small" variant="outlined" color="primary" />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2">-</Typography>
              )}
            </InfoRow>

            <InfoRow icon={<AccountTree fontSize="small" sx={{ color: '#adb5bd' }} />} label="상위 분류 ID">
              <Typography variant="body2">
                {item.parent_id ? String(item.parent_id) : '없음 (최상위)'}
              </Typography>
            </InfoRow>

            <InfoRow icon={<Sort fontSize="small" sx={{ color: '#adb5bd' }} />} label="정렬 순서">
              <Typography variant="body2">
                {item.sort_order != null ? String(item.sort_order) : '-'}
              </Typography>
            </InfoRow>
          </Stack>

          <Divider />

          {/* 작성 정보 + 버튼 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              작성자: {item.write_name} &nbsp;·&nbsp; {new Date(item.created_at).toLocaleDateString()}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={onEdit}
                sx={{ bgcolor: '#4e73df', '&:hover': { bgcolor: '#2e59d9' } }}
              >
                편집
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                삭제
              </Button>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default RiskCateView;
